import { motion } from "framer-motion";
import SceneShell from "../components/SceneShell";
import CountUp from "../components/CountUp";

/**
 * Scene 25 — The new model + new maths.
 * Big "£0.15" reveal, then the new annual cost: 20,000 × £0.15 = £3,000.
 */

export default function Scene25_NewModel() {
  return (
    <SceneShell variant="forest" align="center" data-testid="scene-new-model">
      {/* Lime ambient backlight — this is the WIN */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          opacity: [0.7, 1, 0.7],
        }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(150,193,31,0.22) 0%, transparent 60%)",
        }}
      />

      <div className="relative max-w-[1500px] mx-auto w-full text-center">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="kn-eyebrow mx-auto"
        >
          The new system
        </motion.div>

        {/* Hero: £0.15 */}
        <motion.div
          initial={{ opacity: 0, scale: 0.7, filter: "blur(20px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.4, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 mb-2"
          data-testid="new-model-price"
        >
          <div
            className="kn-display tnum leading-[0.85]"
            style={{
              fontSize: "clamp(120px, 22vw, 320px)",
              background:
                "linear-gradient(135deg, #96C11F 0%, #00deb6 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              textShadow: "0 0 80px rgba(150,193,31,0.35)",
            }}
          >
            <CountUp
              from={1}
              to={0.15}
              prefix="£"
              decimals={2}
              duration={2.0}
              startDelay={1.0}
            />
          </div>
          <div
            className="mt-3 text-base sm:text-lg tracking-[0.18em] uppercase"
            style={{ color: "rgba(255,255,255,0.7)" }}
          >
            per video
          </div>
        </motion.div>

        {/* Recap maths underneath */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 2.4, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 flex flex-wrap items-center justify-center gap-4 sm:gap-6"
          data-testid="new-model-equation"
        >
          <SmallTerm label="Videos / year" value="20,000" />
          <Op symbol="×" />
          <SmallTerm label="Edit cost" value="£0.15" accent="#96C11F" />
          <Op symbol="=" />
          <BigTerm label="Annual edit cost">
            <span className="tnum">
              <CountUp
                from={0}
                to={3000}
                prefix="£"
                duration={1.8}
                startDelay={2.7}
              />
            </span>
          </BigTerm>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 4.4, duration: 0.8 }}
          className="mt-10 inline-flex items-center gap-3 rounded-full px-5 py-3"
          style={{
            background: "rgba(150,193,31,0.10)",
            border: "1px solid rgba(150,193,31,0.45)",
          }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: "#96C11F" }}
          />
          <span
            className="text-sm tracking-[0.18em] uppercase"
            style={{ color: "#96C11F" }}
          >
            £3,000 / year · all in
          </span>
        </motion.div>
      </div>
    </SceneShell>
  );
}

function SmallTerm({ label, value, accent = "#e2e8f0" }) {
  return (
    <div className="rounded-2xl glass px-5 py-4">
      <div
        className="text-[10px] tracking-[0.32em] uppercase mb-1"
        style={{ color: "rgba(255,255,255,0.55)" }}
      >
        {label}
      </div>
      <div
        className="kn-display tnum text-3xl sm:text-4xl whitespace-nowrap"
        style={{ color: accent, fontWeight: 800 }}
      >
        {value}
      </div>
    </div>
  );
}

function BigTerm({ label, children }) {
  return (
    <div className="rounded-2xl glass px-7 py-5 min-w-[240px]">
      <div
        className="text-[10px] tracking-[0.32em] uppercase mb-1"
        style={{ color: "rgba(255,255,255,0.55)" }}
      >
        {label}
      </div>
      <div
        className="kn-display tnum text-4xl sm:text-5xl lg:text-6xl whitespace-nowrap"
        style={{
          background: "linear-gradient(135deg, #96C11F 0%, #00deb6 100%)",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
          fontWeight: 900,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Op({ symbol }) {
  return (
    <span className="kn-display text-3xl sm:text-4xl lg:text-5xl text-white/45">
      {symbol}
    </span>
  );
}
